import { Box, Button, Table, Text } from "@chakra-ui/react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { memo, useMemo } from "react";
import { normalizeRenderConfig, sortReportRows } from "../../store/renderConfig";

const getNextSort = (currentSort, columnId) => {
  if (!currentSort || currentSort.column !== columnId) {
    return { column: columnId, direction: "asc" };
  }
  if (currentSort.direction === "asc") {
    return { column: columnId, direction: "desc" };
  }
  return null;
};

const SortIcon = ({ sort, columnId }) => {
  if (sort?.column !== columnId) return <ArrowUpDown size={14} />;
  return sort.direction === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
};

const ReportDataTable = ({
  columns,
  pinnedColumns = [],
  rows,
  renderConfig,
  renderCell,
  onSortChange,
  emptyMessage = "No data available for this report",
  presortedRows = false,
}) => {
  const config = useMemo(
    () => normalizeRenderConfig(renderConfig, { columnDefinitions: columns }),
    [columns, renderConfig]
  );
  const visibleColumns = useMemo(
    () => [
      ...pinnedColumns,
      ...columns.filter((column) => config.columns.includes(column.id)),
    ],
    [columns, config.columns, pinnedColumns]
  );
  const sortedRows = useMemo(
    () => (presortedRows ? [...rows] : sortReportRows(rows, config, { columnDefinitions: columns })),
    [columns, config, presortedRows, rows]
  );
  const enableSorting = Boolean(onSortChange);
  const tableClassName = `stripedTable reportDataTable reportDataTable--${config.density}`;

  if (!sortedRows.length) {
    return <Text fontSize="sm">{emptyMessage}</Text>;
  }

  return (
    <Box className="reportDataTableScroll">
      <Table.Root className={tableClassName} size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              <Text as="span" className="reportDataTableHeaderText">#</Text>
            </Table.ColumnHeader>
            {visibleColumns.map((column) => (
              <Table.ColumnHeader key={column.id}>
                {column.pinned || !enableSorting ? (
                  <Text as="span" className="reportDataTableHeaderText">{column.label}</Text>
                ) : (
                  <Button
                    className="reportDataTableSortButton"
                    variant="ghost"
                    size="xs"
                    px="0"
                    minW="auto"
                    gap="4px"
                    onClick={() => onSortChange(getNextSort(config.sort, column.id))}
                  >
                    {column.label}
                    <SortIcon sort={config.sort} columnId={column.id} />
                  </Button>
                )}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedRows.map((row, index) => (
            <Table.Row key={row.id || `${row.agentName || "row"}-${index}`}>
              <Table.Cell>{index + 1}</Table.Cell>
              {visibleColumns.map((column) => (
                <Table.Cell key={column.id}>{renderCell(row, column.id)}</Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export default memo(ReportDataTable);
